import { Box, GlobalStyles, SxProps, Theme } from '@mui/material'
import { memo, ReactNode } from 'react'

/**
 * SupplyVue **Scheduler** design system — the separate visual language for the
 * Scheduler module (handoff `Scheduler/design_handoff_rules_and_constraints/`,
 * tokens mirror `scheduler.css`). Direction: light, sharp/squared, data-dense
 * engineering instrument. Type: Archivo (grotesk UI) + JetBrains Mono
 * (times / IDs / numbers).
 *
 * Brand carry-over only: deep teal #00415A (`primary.main`) + blue #0082B3
 * (`secondary.main`). Everything else here is Scheduler-specific and is NOT in
 * the MUI theme, so this module is the single source of truth. Apply it by
 * wrapping a Scheduler surface in <SchedulerThemeScope> (sets the font +
 * tabular figures for all descendants) and using the TOKENS / type sx below.
 */

export const FONT_SANS = "'Archivo', system-ui, -apple-system, sans-serif"
export const FONT_MONO =
  "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace"

export const TOKENS = {
  brandTeal: '#00415A',
  teal700: '#013247',
  teal300: '#4E7689',
  tealTint: '#ECF1F3',
  brandBlue: '#0082B3',
  blue600: '#006A93',
  blue50: '#E7F3F8',
  ink900: '#0E1B22',
  ink700: '#29373F',
  ink600: '#3C4A53',
  ink500: '#51616C',
  ink400: '#71808A',
  ink300: '#97A4AC',
  line300: '#C5D0D7',
  line200: '#DBE2E7',
  line100: '#EAEFF2',
  surface0: '#FFFFFF',
  surface50: '#F3F6F8',
  panel: '#FAFBFC',
  panelHead: '#F0F4F6',
  sidebar: '#f8fafc',
  ganttProduction: '#0082B3',
  ganttClean: '#E0922F',
  ganttPreClean: '#F1C684',
  ganttMaxRunClean: '#8E6FCE',
  ganttChangeover: '#8595A4',
  ganttDowntime: '#C44536',
  ganttIdle: '#EDF1F4',
  stateComplete: '#0E7C86',
  stateRunning: '#C77F2A',
  stateFailed: '#C44536',
  statePending: '#97A4AC',
  amberFg: '#9a5a12',
}

/** Small pill switch shared by Scheduler dialogs (Configure run, Create version). */
export const TogSwitch = memo(
  ({ on, onChange }: { on: boolean; onChange: (v: boolean) => void }) => (
    <Box
      component="button"
      onClick={() => onChange(!on)}
      sx={{
        width: 34,
        height: 18,
        borderRadius: '9px',
        border: 'none',
        cursor: 'pointer',
        p: 0,
        position: 'relative',
        bgcolor: on ? TOKENS.stateComplete : TOKENS.line300,
        transition: 'background 0.14s',
        flexShrink: 0,
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 2,
          left: on ? '18px' : '2px',
          width: 14,
          height: 14,
          borderRadius: '50%',
          bgcolor: '#fff',
          transition: 'left 0.14s',
          boxShadow: '0 1px 2px rgba(14,27,34,0.3)',
        }}
      />
    </Box>
  )
)
TogSwitch.displayName = 'TogSwitch'

export const RADII = { r0: '0px', sm: '2px', md: '3px' }
export const SHADOWS = {
  panel: '0 1px 2px rgba(14,27,34,0.06)',
  pop: '0 6px 22px rgba(14,27,34,0.16)',
}

// ---- typography scale (sx fragments) --------------------------------------
export const TYPE = {
  display: {
    fontFamily: FONT_SANS,
    fontSize: 22,
    fontWeight: 700,
    letterSpacing: '-0.01em',
    lineHeight: 1.15,
    color: TOKENS.ink900,
  },
  h2: {
    fontFamily: FONT_SANS,
    fontSize: 16,
    fontWeight: 700,
    letterSpacing: '-0.005em',
    lineHeight: 1.2,
    color: TOKENS.ink900,
  },
  sectionLabel: {
    fontFamily: FONT_SANS,
    fontSize: 12,
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.08em',
    color: TOKENS.ink500,
  },
  body: {
    fontFamily: FONT_SANS,
    fontSize: 13,
    fontWeight: 400,
    lineHeight: 1.45,
  },
  small: {
    fontFamily: FONT_SANS,
    fontSize: 12,
    fontWeight: 400,
    lineHeight: 1.4,
  },
  micro: {
    fontFamily: FONT_SANS,
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.06em',
    color: TOKENS.ink400,
  },
  mono: {
    fontFamily: FONT_MONO,
    fontSize: 12,
    fontVariantNumeric: 'tabular-nums',
    letterSpacing: '-0.02em',
  },
  data: {
    fontFamily: FONT_MONO,
    fontSize: 12,
    fontWeight: 500,
    fontVariantNumeric: 'tabular-nums',
  },
}

// ─── PanelHead ────────────────────────────────────────────────────────────────
// Shared output-tab header: icon + title/sub + a right-aligned metrics strip.
// Used across FactoryScheduleView.view.tsx's tabs and the mid-week reschedule
// panel — kept here (not in FactoryScheduleView.view.tsx) so both can import
// it without a circular dependency between the two files.

export type PanelMetric = {
  label: string
  value: number | string
  unit?: string
  tone?: string
}

export const PanelHead = memo(
  ({
    icon,
    title,
    sub,
    metrics,
  }: {
    icon: ReactNode
    title: string
    sub?: string
    metrics?: PanelMetric[]
  }) => (
    <Box
      sx={{
        flexShrink: 0,
        borderBottom: `1px solid ${TOKENS.line200}`,
        px: '18px',
        py: '11px',
        display: 'flex',
        alignItems: 'center',
        gap: '14px',
      }}
    >
      <Box
        sx={{
          color: TOKENS.stateComplete,
          display: 'flex',
          alignItems: 'center',
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.18 }}>
        <Box sx={{ ...TYPE.h2 }}>{title}</Box>
        {sub && <Box sx={{ fontSize: 10.5, color: TOKENS.ink400 }}>{sub}</Box>}
      </Box>
      <Box sx={{ flex: 1 }} />
      {metrics && (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {metrics.map((m, i) => (
            <Box
              key={m.label}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '1px',
                pr: i < metrics.length - 1 ? '18px' : 0,
                borderRight:
                  i < metrics.length - 1
                    ? `1px solid ${TOKENS.line200}`
                    : 'none',
                mr: i < metrics.length - 1 ? '18px' : 0,
              }}
            >
              <Box
                sx={{
                  fontSize: 9,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.07em',
                  color: TOKENS.ink400,
                }}
              >
                {m.label}
              </Box>
              <Box
                sx={{
                  fontFamily: FONT_MONO,
                  fontSize: 17,
                  fontWeight: 600,
                  lineHeight: 1,
                  color: m.tone ?? TOKENS.ink900,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {m.value}
                {m.unit && (
                  <Box
                    component="span"
                    sx={{ fontSize: 10, color: TOKENS.ink400, ml: '2px' }}
                  >
                    {m.unit}
                  </Box>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  )
)
PanelHead.displayName = 'PanelHead'

/**
 * Wrap a Scheduler surface to apply the design-system font + tabular figures to
 * everything inside, without touching the global app theme. Use once near the
 * top of a Scheduler page/shell.
 */
/**
 * The data grid's pop-up menus (a row's ⋮ actions, a column header's menu)
 * in the Scheduler language. They render in a portal on <body>, outside
 * <SchedulerThemeScope>'s box, so a scoped sx cannot reach them and they fell
 * back to stock MUI (Roboto, rounded corners, default shadow and spacing).
 * Applied as global styles by the scope instead; this SPA is the Scheduler
 * only, so nothing else shares those classes.
 *
 * Add `className="sch-menu-danger"` to an item that destroys something.
 */
const SCHEDULER_MENU_STYLES = {
  '.MuiDataGrid-menu .MuiPaper-root': {
    backgroundColor: TOKENS.surface0,
    border: `1px solid ${TOKENS.line200}`,
    borderRadius: RADII.md,
    boxShadow: SHADOWS.pop,
    minWidth: 180,
  },
  '.MuiDataGrid-menu .MuiDataGrid-menuList, .MuiDataGrid-menu .MuiList-root': {
    padding: '4px 0',
  },
  '.MuiDataGrid-menu .MuiMenuItem-root': {
    fontFamily: FONT_SANS,
    fontSize: 12.5,
    fontWeight: 500,
    color: TOKENS.ink700,
    minHeight: 32,
    padding: '6px 12px',
    gap: '10px',
    '&:hover, &.Mui-focusVisible': { backgroundColor: TOKENS.blue50 },
    '& .MuiListItemIcon-root': {
      minWidth: 0,
      color: TOKENS.ink500,
      '& svg': { fontSize: 16 },
    },
    '& .MuiListItemText-root, & .MuiTypography-root': {
      fontFamily: FONT_SANS,
      fontSize: 12.5,
      margin: 0,
    },
    '&.Mui-disabled': {
      opacity: 1,
      color: TOKENS.ink300,
      '& .MuiListItemIcon-root': { color: TOKENS.ink300 },
    },
    '&.sch-menu-danger': {
      color: TOKENS.stateFailed,
      '& .MuiListItemIcon-root': { color: TOKENS.stateFailed },
      '&:hover, &.Mui-focusVisible': { backgroundColor: '#FBEDEB' },
      '&.Mui-disabled': { color: TOKENS.ink300 },
      '&.Mui-disabled .MuiListItemIcon-root': { color: TOKENS.ink300 },
    },
  },
  '.MuiDataGrid-menu .MuiDivider-root': {
    margin: '4px 0',
    borderColor: TOKENS.line100,
  },
} as const

export const SchedulerThemeScope = ({
  children,
  sx,
}: {
  children: ReactNode
  sx?: SxProps<Theme>
}) => (
  <Box
    sx={{
      fontFamily: FONT_SANS,
      color: TOKENS.ink900,
      fontFeatureSettings: '"tnum" 1',
      // Archivo for everything in the Scheduler surface — MUI text, form
      // controls and the data grids (which otherwise keep the global Roboto).
      '& .MuiTypography-root, & .MuiButton-root, & .MuiInputBase-root, & .MuiChip-root, & .MuiMenuItem-root':
        { fontFamily: FONT_SANS },
      ...SCHEDULER_GRID_SX,
      ...sx,
    }}
  >
    <GlobalStyles styles={SCHEDULER_MENU_STYLES} />
    {children}
  </Box>
)

/**
 * Compact, squared, data-dense MUI X DataGrid styling in the Scheduler
 * language. Applied within <SchedulerThemeScope> so every grid on a Scheduler
 * surface matches the design without touching grids elsewhere in the app.
 *
 * Per-column accents are opt-in via these cell classes (set `cellClassName` /
 * `headerClassName` on the column): `sch-mono` (tabular mono numerals),
 * `sch-link` (blue mono identifier), `sch-num` (right-aligned mono).
 */
export const SCHEDULER_GRID_SX = {
  // Everything is nested under .MuiDataGrid-root so these selectors out-specify
  // the shared `Common/MUITableV2` Table styled() (which paints headers
  // secondary.main/white, alt rows #f5f5f5 and editable cells #CBD5E1). Where
  // that component uses !important (pinned header, editable cell) we match it.
  '& .MuiDataGrid-root': {
    border: 'none',
    fontFamily: FONT_SANS,
    color: TOKENS.ink900,
    '--DataGrid-rowBorderColor': TOKENS.line100,

    '& .MuiDataGrid-columnHeaders': {
      borderBottom: `1px solid ${TOKENS.line300}`,
    },
    // Light panel-head grey headers with grey uppercase labels (incl. the
    // pinned checkbox header, which the base Table forces to teal/white).
    '& .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeader--pinnedLeft': {
      backgroundColor: `${TOKENS.panelHead} !important`,
      color: `${TOKENS.ink400} !important`,
      borderRadius: 0,
    },
    '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within':
      { outline: 'none' },
    '& .MuiDataGrid-columnHeaderTitle': {
      fontFamily: FONT_SANS,
      fontSize: 10.5,
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      color: TOKENS.ink400,
    },
    '& .MuiDataGrid-iconButtonContainer, & .MuiDataGrid-sortIcon, & .MuiDataGrid-menuIconButton':
      { color: TOKENS.ink400 },
    '& .MuiDataGrid-columnSeparator': { display: 'none' },

    // The row's ⋮ actions trigger: quiet until hovered, squared like the rest.
    '& .MuiDataGrid-actionsCell .MuiIconButton-root': {
      color: TOKENS.ink400,
      borderRadius: RADII.md,
      padding: '4px',
      '&:hover': { backgroundColor: TOKENS.line100, color: TOKENS.ink700 },
      '& svg': { fontSize: 18 },
    },
    '& .MuiDataGrid-cell': {
      fontFamily: FONT_SANS,
      fontSize: 12,
      color: TOKENS.ink900,
      '&:focus, &:focus-within': { outline: 'none' },
    },
    // Clean look — drop the heavy vertical borders.
    '& .MuiDataGrid-cell--withRightBorder, & .MuiDataGrid-columnHeader--withRightBorder':
      { borderRight: 'none' },
    // Alternating rows in the Scheduler surface palette.
    '& .MuiDataGrid-row.row--odd, & .MuiDataGrid-row.row--odd .MuiDataGrid-cell':
      { backgroundColor: TOKENS.surface50 },
    '& .MuiDataGrid-row.row--even, & .MuiDataGrid-row.row--even .MuiDataGrid-cell':
      { backgroundColor: TOKENS.surface0 },
    '& .MuiDataGrid-row:hover, & .MuiDataGrid-row:hover .MuiDataGrid-cell': {
      backgroundColor: TOKENS.blue50,
    },
    // The base Table flags editable cells with a strong #CBD5E1 fill; the
    // design keeps them clean, so neutralise it (still beatable per-table).
    '& .MuiDataGrid-row .MuiDataGrid-cell--editable': {
      backgroundColor: 'transparent !important',
    },

    '& .MuiDataGrid-footerContainer': {
      fontFamily: FONT_SANS,
      minHeight: 40,
      borderTop: `1px solid ${TOKENS.line200}`,
    },
    '& .MuiTablePagination-root, & .MuiTablePagination-root *': {
      fontFamily: FONT_SANS,
      fontSize: 12,
    },

    // Opt-in per-column accents (set cellClassName on the column).
    '& .sch-mono': {
      fontFamily: FONT_MONO,
      fontVariantNumeric: 'tabular-nums',
      letterSpacing: '-0.02em',
    },
    '& .sch-num': {
      fontFamily: FONT_MONO,
      fontVariantNumeric: 'tabular-nums',
      letterSpacing: '-0.02em',
      justifyContent: 'flex-end',
      textAlign: 'right',
    },
    '& .sch-link': {
      fontFamily: FONT_MONO,
      color: TOKENS.blue600,
      fontWeight: 600,
      letterSpacing: '-0.02em',
    },
    // "This row needs this field filled in." Deliberately an amber prompt
    // rather than MUI's error state: an error on a cell BLOCKS the edit from
    // committing, which is how a carry-over quantity used to get silently
    // eaten. This says the same thing without standing in the way.
    '& .sch-needs-input': {
      backgroundColor: '#FDF6E7',
      boxShadow: `inset 2px 0 0 ${TOKENS.stateRunning}`,
    },
  },
}

// ---- shared presentational primitives -------------------------------------

/** Big page/section title (Archivo 22/700). */
export const SchDisplay = ({
  children,
  sx,
}: {
  children: ReactNode
  sx?: SxProps<Theme>
}) => <Box sx={{ ...TYPE.display, ...sx }}>{children}</Box>

/** Uppercase tracked section label (sch-h3). */
export const SectionLabel = ({
  children,
  sx,
}: {
  children: ReactNode
  sx?: SxProps<Theme>
}) => <Box sx={{ ...TYPE.sectionLabel, ...sx }}>{children}</Box>

type ChipTone =
  | 'neutral'
  | 'teal'
  | 'blue'
  | 'amber'
  | 'complete'
  | 'running'
  | 'failed'
const CHIP_TONES: Record<ChipTone, { bg: string; fg: string; bd: string }> = {
  neutral: { bg: TOKENS.surface50, fg: TOKENS.ink700, bd: TOKENS.line300 },
  teal: { bg: TOKENS.tealTint, fg: TOKENS.brandTeal, bd: TOKENS.teal300 },
  blue: { bg: TOKENS.blue50, fg: TOKENS.blue600, bd: TOKENS.brandBlue },
  amber: {
    bg: 'rgba(224,146,47,0.12)',
    fg: TOKENS.amberFg,
    bd: 'rgba(224,146,47,0.6)',
  },
  complete: {
    bg: 'rgba(14,124,134,0.10)',
    fg: TOKENS.stateComplete,
    bd: TOKENS.stateComplete,
  },
  running: {
    bg: 'rgba(199,127,42,0.12)',
    fg: TOKENS.stateRunning,
    bd: TOKENS.stateRunning,
  },
  failed: {
    bg: 'rgba(196,69,54,0.10)',
    fg: TOKENS.stateFailed,
    bd: TOKENS.stateFailed,
  },
}

/** Small squared status/label chip in the Scheduler language. */
export const SchChip = ({
  children,
  tone = 'neutral',
  mono,
  icon,
  sx,
}: {
  children: ReactNode
  tone?: ChipTone
  mono?: boolean
  icon?: ReactNode
  sx?: SxProps<Theme>
}) => {
  const t = CHIP_TONES[tone]
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        background: t.bg,
        color: t.fg,
        border: `1px solid ${t.bd}`,
        borderRadius: RADII.md,
        px: '7px',
        py: '1px',
        fontSize: 10.5,
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        fontFamily: mono ? FONT_MONO : FONT_SANS,
        whiteSpace: 'nowrap',
        lineHeight: 1.6,
        '& svg': { fontSize: 13 },
        ...sx,
      }}
    >
      {icon}
      {children}
    </Box>
  )
}

/**
 * Button variants, transcribed from the design handoff's button matrix
 * (`design_handoff_rules_and_constraints/scheduler/preview/comp-buttons.html`).
 *
 * The vocabulary is the matrix's, which required renaming two of the three
 * kinds this component used to take — they described the same shapes under
 * swapped names, which is exactly the sort of thing that makes a design
 * system stop being one:
 *
 * - what was `outline` (white, bordered) is the matrix's **ghost**
 * - what was `ghost` (transparent) is the matrix's **subtle**
 *
 * All call sites were migrated with the rename. `secondary` and `danger` are
 * new; the matrix has always had them and this component did not, which is
 * why the table toolbar had to hand-roll a red Delete.
 */
type PillKind = 'primary' | 'secondary' | 'ghost' | 'subtle' | 'danger'

/** Height, type size, padding and icon size per matrix size. The heights are
 *  fixed on purpose: a row of buttons that sizes itself from padding never
 *  lines up, which is what the matrix's 32/24/20 exists to prevent. */
const PILL_SIZES = {
  md: { height: 32, fontSize: 12.5, padding: '0 13px', icon: 16 },
  sm: { height: 24, fontSize: 11.5, padding: '0 9px', icon: 13 },
  xs: { height: 20, fontSize: 10.5, padding: '0 7px', icon: 12 },
} as const

const PILL_KINDS: Record<
  PillKind,
  { bg: string; fg: string; bd: string; hoverBg: string; hoverBd?: string }
> = {
  primary: {
    bg: TOKENS.brandTeal,
    fg: '#fff',
    bd: TOKENS.brandTeal,
    hoverBg: TOKENS.teal700,
    hoverBd: TOKENS.teal700,
  },
  secondary: {
    bg: '#E3ECEF',
    fg: TOKENS.brandTeal,
    bd: 'transparent',
    hoverBg: '#D3E0E5',
  },
  ghost: {
    bg: TOKENS.surface0,
    fg: TOKENS.brandTeal,
    bd: TOKENS.line300,
    hoverBg: TOKENS.tealTint,
    hoverBd: TOKENS.teal300,
  },
  subtle: {
    bg: 'transparent',
    fg: TOKENS.ink700,
    bd: 'transparent',
    hoverBg: TOKENS.line100,
  },
  danger: {
    bg: TOKENS.stateFailed,
    fg: '#fff',
    bd: TOKENS.stateFailed,
    hoverBg: '#A83A30',
    hoverBd: '#A83A30',
  },
}

export const PillButton = ({
  children,
  kind = 'subtle',
  size = 'sm',
  icon,
  onClick,
  disabled,
  type,
  title,
  sx,
}: {
  children?: ReactNode
  kind?: PillKind
  size?: 'xs' | 'sm' | 'md'
  icon?: ReactNode
  onClick?: () => void
  disabled?: boolean
  type?: 'button' | 'submit'
  /** Native tooltip. Added for the role gate: a disabled button with no
   *  explanation reads as a bug, and the reason a control is unavailable
   *  (a role that does not permit the action) is worth saying. */
  title?: string
  sx?: SxProps<Theme>
}) => {
  const dim = PILL_SIZES[size]
  const tone = PILL_KINDS[kind]
  return (
    <Box
      component="button"
      type={type ?? 'button'}
      onClick={onClick}
      disabled={disabled}
      title={title}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px',
        flexShrink: 0,
        height: dim.height,
        padding: dim.padding,
        fontSize: dim.fontSize,
        background: tone.bg,
        color: tone.fg,
        border: `1px solid ${tone.bd}`,
        borderRadius: RADII.md,
        cursor: 'pointer',
        fontFamily: FONT_SANS,
        fontWeight: 600,
        letterSpacing: '.01em',
        whiteSpace: 'nowrap',
        transition: 'all .12s',
        '& svg': { fontSize: dim.icon },
        '&:hover': {
          background: tone.hoverBg,
          ...(tone.hoverBd ? { borderColor: tone.hoverBd } : {}),
        },
        // Keyboard focus only. The matrix specifies a ring; giving it to
        // every mouse click as well would put a halo round Save on each
        // press.
        '&:focus-visible': {
          outline: 'none',
          boxShadow: '0 0 0 3px rgba(0,130,179,.28)',
        },
        '&:disabled': { opacity: 0.4, cursor: 'not-allowed' },
        ...sx,
      }}
    >
      {icon}
      {children}
    </Box>
  )
}

/**
 * Small on/off pill, styled to match the week-selector squares it sits beside.
 * Used for the toggles that reveal an optional part of an input tab — the
 * carry-over columns on production requirements, the trials table on the
 * downtime tab — so the normal-flow view stays uncluttered.
 */
export const PillToggle = ({
  label,
  on,
  onClick,
  title,
}: {
  label: string
  on: boolean
  onClick: () => void
  title?: string
}) => (
  <Box
    component="button"
    onClick={onClick}
    title={title}
    sx={{
      height: 26,
      px: 1.25,
      border: '1px solid',
      borderColor: on ? TOKENS.brandTeal : TOKENS.line300,
      background: on ? TOKENS.brandTeal : '#fff',
      color: on ? '#fff' : TOKENS.ink500,
      borderRadius: '2px',
      fontSize: 11,
      fontWeight: 700,
      cursor: 'pointer',
      fontFamily: FONT_SANS,
    }}
  >
    {label}
  </Box>
)

// Back-compat alias used by the timeline primitives module.
export const MONO = FONT_MONO
